import * as _ from 'lodash';
import { useMemo, type VoidFunctionComponent } from 'react';

import useGetRevisionConclusions from '../../../hooks/api/correctionsV2/useGetRevisionConclusions';
import useIsLicenseConcludedEnabled from '../../../hooks/features/useIsLicenseConcludedEnabled';
import { ConclusionBanner } from '../dependenciesV2/DependencyDrawer/content/DependencyLicenses/ConclusionBanner';
import type { LicenseTabScope } from '../dependenciesV2/DependencyDrawer/content/DependencyLicenses/types';

type Props = {
  revisionLocator: string;
  projectLocator: string;
  /** How the project is named in the confirm dialogs, e.g. "my-app 1a2b3c4". */
  packageName: string;
  organizationId: number;
  allLicenseIds: string[];
  canEditLicenses: boolean;
};

export const ProjectConclusionBanner: VoidFunctionComponent<Props> = ({
  revisionLocator,
  projectLocator,
  packageName,
  organizationId,
  allLicenseIds,
  canEditLicenses,
}) => {
  const isLicenseConcludedEnabled = useIsLicenseConcludedEnabled(organizationId);
  const { data: conclusions } = useGetRevisionConclusions(revisionLocator);

  const scope: LicenseTabScope = useMemo(() => ({
    dependencyRevisionLocator: revisionLocator,
    type: 'revision',
    projectLocator,
    revisionLocator,
  }), [revisionLocator, projectLocator]);

  const allLicenses = useMemo(
    () => new Set(allLicenseIds),
    [allLicenseIds],
  );

  if (!isLicenseConcludedEnabled || !conclusions || _.isEmpty(allLicenses) || _.isEmpty(conclusions.scoped.licenses)) {
    return null;
  }

  return (
    <ConclusionBanner
      dependencyRevisionLocator={revisionLocator}
      scope={scope}
      concludedLicenses={conclusions}
      allLicenses={allLicenses}
      canEditLicenses={canEditLicenses}
      packageName={packageName}
      entityName="project"
    />
  );
};
